import React, { useState, useEffect } from 'react';
import { useElevate } from '../../Elevate/ElevateContext';
import { ElevateRecommendationsHandler } from '../../Elevate/pdp/ElevateRecommendationsHandler';
import { styled } from 'linaria/react';
import { useLocation } from 'react-router-dom';
import { ElevateSkeletonProductCard } from '../../Elevate/ElevateSkeletonProductCard';
import { theme } from '../../Theme';
import { Above } from '@jetshop/ui/Breakpoints';

const ElevateRecommendedProductsWrapper = styled('div')`
  padding: 0 1rem;
`;

const ContentFade = styled('div')`
  animation: elevateRecProductsFadeIn 480ms ease-out forwards;
  opacity: 0;

  @keyframes elevateRecProductsFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SkeletonRoot = styled('div')`
  /* margin-top: 45px; */
  max-width: 90rem;
  margin-left: auto;
  margin-right: auto;

  @keyframes elevateRecShimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }

  .elevate-rec-sk {
    background: linear-gradient(90deg, #ebe8e3 0%, #f6f4f1 40%, #ebe8e3 80%);
    background-size: 200% 100%;
    animation: elevateRecShimmer 1.15s ease-in-out infinite;
    border-radius: 4px;
  }

  .elevate-rec-sk-title {
    height: 24px;
    width: min(280px, 42%);
    margin-bottom: 20px;
  }

  .elevate-rec-sk-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    column-gap: 10px;
    row-gap: 0;
    list-style: none;
    padding: 0;
    margin: 0;

    @media screen and (max-width: 64rem) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .elevate-rec-sk-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .elevate-rec-sk-img {
    aspect-ratio: 1 / 1;
    width: 100%;
    border-radius: 2px;
  }

  .elevate-rec-sk-line {
    height: 12px;
    width: 100%;
  }

  .elevate-rec-sk-line--short {
    width: 72%;
  }

  .elevate-rec-sk-line--shorter {
    width: 48%;
  }
`;

const SKELETON_CARD_COUNT_DESKTOP = 4;
const SKELETON_CARD_COUNT_MOBILE = 2;

const resolveTitle = title => {
  if (title != null && typeof title === 'object' && 'value' in title) {
    return title.value ?? null;
  }
  if (typeof title === 'string') {
    return title;
  }
  return null;
};

const ElevateRecommendedProductsSkeleton = () => (
  <Above breakpoint="lg">
    {matches => (
      <SkeletonRoot
        className="elevate-recommended-products-skeleton"
        aria-hidden="true"
      >
        <div className="elevate-rec-sk elevate-rec-sk-title" />
        <ul className="elevate-rec-sk-grid">
          {Array.from({
            length: matches
              ? SKELETON_CARD_COUNT_DESKTOP
              : SKELETON_CARD_COUNT_MOBILE
          }).map((_, i) => (
            <ElevateSkeletonProductCard key={i} />
          ))}
        </ul>
      </SkeletonRoot>
    )}
  </Above>
);

const getCategoryPathFromCategoryId = async categoryId => {
  if (!Number.isFinite(categoryId)) return null;

  try {
    const response = await fetch(
      `/Services/Rest/v2/JSON/sv-SE/categories/${categoryId}`
    );
    if (!response.ok) return null;

    const data = await response.json();
    const categoryPath = data?.[0]?.CategoryUrl;
    if (!categoryPath) return null;

    if (categoryPath.startsWith('http')) {
      return new URL(categoryPath).pathname;
    }

    return categoryPath.split('?')[0] || null;
  } catch {
    return null;
  }
};

// STARTPAGE COMPONENT
export const ElevateRecommendedProducts = ({
  id,
  algorithm,
  title,
  path = '/homepage',
  slidesToShowDesktop = { value: 4 },
  slidesToShowMobile = { value: 2 }
}) => {
  const [elevateRecommendedProducts, setElevateRecommendedProducts] =
    useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getLandingPageRecommendationsData } = useElevate();
  const location = useLocation();
  const eId = id?.value ?? id;
  const eAlgorithm = algorithm?.value ?? algorithm;
  const eTitle = resolveTitle(title);
  const configuredPath = path?.value ?? path;
  const isPreview = location.pathname.startsWith('/preview');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    const fetchData = async () => {
      let pageReference = configuredPath;

      if (isPreview) {
        const searchParams = new URLSearchParams(location.search);
        const categoryId = Number(searchParams.get('categoryid'));
        const pathname = await getCategoryPathFromCategoryId(categoryId);
        if (pathname) {
          pageReference = pathname;
        }
      }

      try {
        const response = await getLandingPageRecommendationsData({
          pageReference,
          listId: eId,
          algorithm: eAlgorithm
        });
        if (isMounted) {
          setElevateRecommendedProducts(response);
        }
      } catch (error) {
        console.error('ElevateRecommendedProducts failed', error);
        if (isMounted) {
          setElevateRecommendedProducts(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [
    configuredPath,
    isPreview,
    location.search,
    getLandingPageRecommendationsData,
    eId,
    eAlgorithm
  ]);

  const hasLists = elevateRecommendedProducts?.recommendationLists?.length > 0;

  if (!isLoading && !hasLists) {
    return null;
  }

  return (
    <ElevateRecommendedProductsWrapper
      className={'elevate-recommended-products'}
    >
      {isLoading && <ElevateRecommendedProductsSkeleton />}
      {!isLoading && hasLists && (
        <ContentFade>
          <ElevateRecommendationsHandler
            noTitle={!eTitle}
            title={eTitle}
            recommendationsLists={
              elevateRecommendedProducts?.recommendationLists
            }
            slidesToShowDesktop={slidesToShowDesktop?.value}
            slidesToShowMobile={slidesToShowMobile?.value}
          />
        </ContentFade>
      )}
    </ElevateRecommendedProductsWrapper>
  );
};
